(() => {
  'use strict';

  const CONTRACT = Object.freeze({
    productId: '9367564943596',
    handle: 'golf-swag-players-pack-v8',
    parentVariantId: '48132142530796',
    parentSku: '34025',
    outputContract: 'native-nested-v1',
    templates: Object.freeze([
      'c487d0bb-68fb-4ff9-ac91-2874726501a8',
      '498619db-7077-4e11-9a8b-f81cc44b647a',
    ]),
    variants: Object.freeze({
      '48132142530796': Object.freeze({ role: 'parent', type: 'parent', sku: '34025', handle: 'golf-swag-players-pack-v8' }),
      '46726732185836': Object.freeze({ role: 'component', type: 'ball', sku: '6553', handle: 'titleist-pro-v1' }),
      '46726731989228': Object.freeze({ role: 'component', type: 'towel', sku: '13679', handle: 'the-maggy-magnet-towel' }),
      '46796526747884': Object.freeze({ role: 'component', type: 'pitchfix', sku: '28016', handle: 'pitchfix-original-divot-tool' }),
    }),
  });

  const REQUIRED_COMMON_PROPERTIES = Object.freeze([
    '_customily-thumb-id',
    '_customily-preview',
    '_customily-thumb',
    '_customily-customization-url',
  ]);
  const NUMBERED_OUTPUT = /^_customily-(eps-name|production-url|personalization-id)(?:-(\d+))?$/;
  const MARKER_PROPERTY = '_gs_players_pack_contract';
  const PRODUCT_PROPERTY = '_gs_players_pack_product_id';
  const GROUP_PROPERTY = '_bundle_group_id';
  const PARENT_SKU_PROPERTY = '_bundle_parent_sku';
  const ROLE_PROPERTY = '_bundle_role';
  const COMPONENT_TYPE_PROPERTY = '_bundle_component_type';
  const UPLOAD_PROPERTY_BY_METHOD = Object.freeze({
    'Upload Image': '_Upload Image ',
    'Upload Image & Remove Background': '_Upload Image + Remove Background',
    'Upload Face Image': '_Facecut',
    'Upload Logo': '_Upload Logo',
  });
  const METHOD_PROPERTY_BY_COMPONENT = Object.freeze({
    ball: 'Image Style',
    towel: 'Image Style ',
    pitchfix: 'Images',
  });
  const SECTION_LABEL_BY_COMPONENT = Object.freeze({
    ball: 'Choose Style for Golf Ball',
    towel: 'Choose Style for Maggy Towel',
    pitchfix: 'Choose Style for Divot Tool',
  });
  const GUIDED_SCOPE_BY_COMPONENT = Object.freeze({
    ball: 'balls',
    towel: 'towel',
    pitchfix: 'pitchfix',
  });
  const COMPONENT_UPLOAD_PROPERTIES = Object.freeze(Object.values(UPLOAD_PROPERTY_BY_METHOD));
  const COMPONENT_HANDOFF_TTL_MS = 120000;
  const BLOCKED_MESSAGE = 'This Players Pack could not be verified. Refresh the cart or remove the pack before checkout.';
  let presentationRevision = 0;
  let lastValidatedPresentationCart = null;

  const asId = value => (value === undefined || value === null ? '' : String(value));
  const lineVariantId = line => asId(line?.variant_id ?? line?.variantId ?? line?.id);
  const lineProperties = line => (line?.properties && typeof line.properties === 'object' ? line.properties : {});
  const routingProperty = (properties, privateKey) => properties[privateKey] ?? properties[privateKey.slice(1)];
  const bundleGroupId = properties => routingProperty(properties, GROUP_PROPERTY);
  const bundleRole = properties => routingProperty(properties, ROLE_PROPERTY);
  const bundleComponentType = properties => routingProperty(properties, COMPONENT_TYPE_PROPERTY);
  const isMarkedLine = line =>
    lineProperties(line)[MARKER_PROPERTY] === CONTRACT.outputContract &&
    asId(lineProperties(line)[PRODUCT_PROPERTY]) === CONTRACT.productId;

  function uniqueGroupId() {
    if (globalThis.crypto?.randomUUID) return `players-pack-${globalThis.crypto.randomUUID()}`;
    if (!globalThis.crypto?.getRandomValues) throw new Error('Secure bundle ID generation is unavailable.');
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    return `players-pack-${Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')}`;
  }

  function withoutRoutingProperties(source) {
    const properties = {};
    for (const [key, value] of Object.entries(source)) {
      if (key.startsWith('bundle_') || key.startsWith('_bundle_') || key.startsWith('_gs_players_pack_')) continue;
      properties[key] = value;
    }
    return properties;
  }

  // The proven theme lane keys grouping, counting, quantity sync, and fail-closed
  // handling on the unprefixed `bundle_*` names; `_gs_*` markers stay
  // underscore-prefixed so Shopify hides them everywhere.
  function nativeRoutingProperties(groupId, definition) {
    return {
      [MARKER_PROPERTY]: CONTRACT.outputContract,
      [PRODUCT_PROPERTY]: CONTRACT.productId,
      [GROUP_PROPERTY]: groupId,
      [PARENT_SKU_PROPERTY]: CONTRACT.parentSku,
      [ROLE_PROPERTY]: definition.role,
      [COMPONENT_TYPE_PROPERTY]: definition.type,
    };
  }

  function outputRecords(parentProperties) {
    const records = new Map();
    for (const [key, value] of Object.entries(parentProperties)) {
      const match = key.match(NUMBERED_OUTPUT);
      if (!match || value === '' || value === null || value === undefined) continue;
      const slot = match[2] ?? '0';
      const record = records.get(slot) ?? {};
      record[match[1]] = value;
      records.set(slot, record);
    }
    return [...records.values()];
  }

  function assertTruthyProperties(properties, keys, label) {
    for (const key of keys) {
      if (!properties[key]) throw new Error(`${label} is missing ${key}.`);
    }
  }

  function templateIdFromCustomizationUrl(value) {
    try {
      const templateId = new URL(value, location.origin).searchParams.get('templateId');
      if (!CONTRACT.templates.includes(templateId)) throw new Error('unsupported template');
      return templateId;
    } catch (_) {
      throw new Error('Players Pack has an unsupported Customily template.');
    }
  }

  const canonicalUploadProperty = key => String(key ?? '').replace(/\u200b+$/g, '');
  const isComponentUploadProperty = key => COMPONENT_UPLOAD_PROPERTIES.includes(canonicalUploadProperty(key));

  function uploadContract(parentProperties, componentUploadKeys = {}) {
    const mode = parentProperties['Choose if you want same image on all or different'];
    if (mode === 'Same image on all 3 products') {
      // Same Design exposes one shared Customily method scope. Depending on
      // which component section owns that visible scope, FormData contains one
      // of the three component method-property names—not all three.
      const selectedMethods = Object.values(METHOD_PROPERTY_BY_COMPONENT)
        .map(methodProperty => parentProperties[methodProperty])
        .filter(method => method !== undefined && method !== null && method !== '');
      if (!selectedMethods.length || selectedMethods.some(method => !UPLOAD_PROPERTY_BY_METHOD[method])) {
        throw new Error('Players Pack shared image method is missing or unsupported.');
      }
      const uniqueMethods = [...new Set(selectedMethods)];
      if (uniqueMethods.length !== 1) throw new Error('Players Pack shared image method is inconsistent.');
      const uploadProperty = UPLOAD_PROPERTY_BY_METHOD[uniqueMethods[0]];
      const populatedUploads = Object.entries(parentProperties)
        .filter(([key, value]) => isComponentUploadProperty(key) && value)
        .map(([key, value]) => ({ property: canonicalUploadProperty(key), value }));
      const urls = [...new Set(populatedUploads
        .filter(upload => upload.property === uploadProperty)
        .map(upload => upload.value))];
      if (urls.length !== 1) throw new Error('Players Pack shared image upload is missing or inconsistent.');
      const url = urls[0];
      return {
        ball: [uploadProperty, url],
        towel: [uploadProperty, url],
        pitchfix: [uploadProperty, url],
      };
    }
    if (mode === 'Different image on all 3 products') {
      const uploads = {};
      for (const [component, methodProperty] of Object.entries(METHOD_PROPERTY_BY_COMPONENT)) {
        const expectedProperty = UPLOAD_PROPERTY_BY_METHOD[parentProperties[methodProperty]];
        const sourceProperty = componentUploadKeys[component];
        if (!expectedProperty || !sourceProperty || canonicalUploadProperty(sourceProperty) !== expectedProperty) {
          throw new Error(`Players Pack ${component} upload mapping is incomplete.`);
        }
        const url = parentProperties[sourceProperty];
        if (!url) throw new Error(`Players Pack ${component} upload is missing.`);
        uploads[component] = [expectedProperty, url];
      }
      return uploads;
    }
    throw new Error('Players Pack image allocation is unsupported.');
  }

  function normalizeCandidateItems(payload, makeGroupId = uniqueGroupId) {
    if (!payload || !Array.isArray(payload.items)) throw new Error('Players Pack add payload must contain an items array.');
    const parentMatches = payload.items.filter(item => lineVariantId(item) === CONTRACT.parentVariantId);
    if (parentMatches.length !== 1) throw new Error('Players Pack add must contain exactly one parent line.');

    const parent = parentMatches[0];
    const parentProperties = { ...lineProperties(parent) };
    const parentQuantity = Number(parent.quantity ?? 1);
    if (!Number.isInteger(parentQuantity) || parentQuantity < 1 || parentQuantity > 999) {
      throw new Error('Players Pack quantity is invalid.');
    }
    assertTruthyProperties(parentProperties, REQUIRED_COMMON_PROPERTIES, 'Players Pack parent');
    if (!parentProperties['_customily-upsell-main-id']) throw new Error('Players Pack parent is missing its Customily upsell identity.');

    const expectedIds = Object.keys(CONTRACT.variants);
    if (payload.items.length !== expectedIds.length) throw new Error('Players Pack add must contain one parent and exactly three components.');
    for (const variantId of expectedIds) {
      if (payload.items.filter(item => lineVariantId(item) === variantId).length !== 1) {
        throw new Error(`Players Pack add has an invalid ${CONTRACT.variants[variantId].sku} line count.`);
      }
    }

    const records = outputRecords(parentProperties);
    for (const variantId of expectedIds) {
      const source = payload.items.find(item => lineVariantId(item) === variantId);
      const definition = CONTRACT.variants[variantId];
      if (!source || !definition) throw new Error('Players Pack component mapping failed.');
      const sourceProperties = { ...lineProperties(source) };
      const sourceQuantity = Number(source.quantity ?? 1);
      if (!Number.isInteger(sourceQuantity) || sourceQuantity !== parentQuantity) {
        throw new Error(`Players Pack ${definition.sku} quantity does not match its parent.`);
      }
      if (definition.role === 'component') {
        if (sourceProperties['_customily-upsell-id'] !== parentProperties['_customily-upsell-main-id']) {
          throw new Error(`Players Pack ${definition.sku} has an inconsistent Customily upsell identity.`);
        }
        const matches = records.filter(record => record['eps-name'] === definition.handle);
        if (matches.length !== 1 || !matches[0]['production-url'] || !matches[0]['personalization-id']) {
          throw new Error(`Players Pack ${definition.sku} does not have one complete component-owned Customily output.`);
        }
      }
    }

    templateIdFromCustomizationUrl(parentProperties['_customily-customization-url']);
    const uploads = uploadContract(parentProperties, parent._gsComponentUploadKeys);
    const groupId = makeGroupId();
    if (!groupId || typeof groupId !== 'string') throw new Error('Players Pack bundle ID generation failed.');

    const items = [];
    for (const [variantId, definition] of Object.entries(CONTRACT.variants)) {
      const source = payload.items.find(item => lineVariantId(item) === variantId);
      const properties = withoutRoutingProperties(lineProperties(source));
      if (definition.role === 'parent') {
        for (const key of Object.keys(properties)) {
          if (NUMBERED_OUTPUT.test(key) || isComponentUploadProperty(key)) delete properties[key];
        }
        Object.assign(properties, nativeRoutingProperties(groupId, definition));
        items.push({ id: variantId, quantity: parentQuantity, properties });
        continue;
      }
      for (const key of Object.keys(properties)) {
        if (NUMBERED_OUTPUT.test(key) || isComponentUploadProperty(key)) delete properties[key];
      }
      const record = records.find(outputRecord => outputRecord['eps-name'] === definition.handle);
      for (const key of REQUIRED_COMMON_PROPERTIES) properties[key] = parentProperties[key];
      properties['_customily-eps-name'] = record['eps-name'];
      properties['_customily-production-url'] = record['production-url'];
      properties['_customily-personalization-id'] = record['personalization-id'];
      const [uploadProperty, url] = uploads[definition.type];
      properties[uploadProperty] = url;
      Object.assign(properties, nativeRoutingProperties(groupId, definition));
      items.push({ id: variantId, quantity: parentQuantity, parent_id: CONTRACT.parentVariantId, properties });
    }
    return { ...payload, items, groupId, quantity: parentQuantity };
  }

  function groupLines(cart, groupId) {
    return (Array.isArray(cart?.items) ? cart.items : []).filter(
      item => isMarkedLine(item) && bundleGroupId(lineProperties(item)) === groupId
    );
  }

  function validateGroup(cart, groupId, expectedQuantity, { allowMissing = false } = {}) {
    const lines = groupLines(cart, groupId);
    if (allowMissing && lines.length === 0) return { valid: true, lines, parent: null };
    if (lines.length !== 4) return { valid: false, reason: 'line-count', lines };
    const seen = new Set();
    let parent = null;
    for (const line of lines) {
      const variantId = lineVariantId(line);
      const definition = CONTRACT.variants[variantId];
      const properties = lineProperties(line);
      if (!definition || seen.has(variantId)) return { valid: false, reason: 'composition', lines };
      seen.add(variantId);
      if (bundleRole(properties) !== definition.role || bundleComponentType(properties) !== definition.type) {
        return { valid: false, reason: 'role', lines };
      }
      if (Number(line.quantity) !== Number(expectedQuantity)) return { valid: false, reason: 'quantity', lines };
      if (definition.role === 'parent') parent = line;
    }
    if (seen.size !== 4 || !parent) return { valid: false, reason: 'composition', lines };
    const parentKey = asId(parent.key);
    for (const line of lines) {
      if (lineVariantId(line) === CONTRACT.parentVariantId) continue;
      const relationshipKey = asId(line.parent_relationship?.parent_key ?? line.parentRelationship?.parentKey);
      if (!parentKey || relationshipKey !== parentKey) return { valid: false, reason: 'relationship', lines };
    }
    return { valid: true, lines, parent };
  }

  function validateMarkedCart(cart) {
    const marked = (Array.isArray(cart?.items) ? cart.items : []).filter(isMarkedLine);
    const groups = [...new Set(marked.map(item => bundleGroupId(lineProperties(item))))];
    if (groups.some(group => !group)) return { valid: false, reason: 'missing-group' };
    for (const group of groups) {
      const lines = groupLines(cart, group);
      const quantity = Number(lines[0]?.quantity);
      const validation = validateGroup(cart, group, quantity);
      if (!validation.valid) return { ...validation, groupId: group };
    }
    return { valid: true, groups };
  }

  function shopperItemCount(items) {
    const cartItems = Array.isArray(items) ? items : [];
    const validation = validateMarkedCart({ items: cartItems });
    return cartItems.reduce((total, item) => {
      const properties = lineProperties(item);
      if (isMarkedLine(item)) {
        const hiddenComponent = validation.valid && bundleRole(properties) === 'component';
        return total + (hiddenComponent ? 0 : Number(item.quantity) || 0);
      }
      const customilyAddon = Boolean(properties['_customily-upsell-id']);
      return total + (customilyAddon ? 0 : Number(item.quantity) || 0);
    }, 0);
  }

  function ordinarySnapshot(cart, excludedGroupId) {
    return (Array.isArray(cart?.items) ? cart.items : [])
      .filter(item => bundleGroupId(lineProperties(item)) !== excludedGroupId)
      .map(item => `${asId(item.key)}\u0000${lineVariantId(item)}\u0000${Number(item.quantity)}`)
      .sort();
  }

  function sameOrdinaryState(before, after, groupId) {
    return JSON.stringify(ordinarySnapshot(before, groupId)) === JSON.stringify(ordinarySnapshot(after, groupId));
  }

  async function readCart(fetchImpl) {
    const response = await fetchImpl('/cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' } });
    if (!response.ok) throw new Error(`Cart read failed with HTTP ${response.status}.`);
    const cart = await response.json();
    if (!cart || !Array.isArray(cart.items)) throw new Error('Cart read returned an invalid payload.');
    return cart;
  }

  function requestedSections(value) {
    const entries = Array.isArray(value) ? value : String(value ?? '').split(',');
    return [...new Set(entries.map(entry => String(entry).trim()).filter(Boolean))];
  }

  let cartSectionReadGeneration = 0;

  async function readSections(fetchImpl, sectionValue, sectionsUrl) {
    const sections = requestedSections(sectionValue);
    if (!sections.length) throw new Error('Cart sections were not requested.');
    // Product and cart routes return their JSON resources when fetched with the
    // JSON Accept header, even when a `sections` parameter is present. Use the
    // locale-aware storefront root as the neutral Section Rendering endpoint.
    const sectionRoute = globalThis.Shopify?.routes?.root || '/';
    const url = new URL(sectionRoute, location.origin);
    url.searchParams.set('sections', sections.join(','));
    url.searchParams.set('_gs_cart_sections', `${Date.now()}-${++cartSectionReadGeneration}`);
    const response = await fetchImpl(url.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
    });
    if (!response.ok) throw new Error(`Cart section read failed with HTTP ${response.status}.`);
    const bodies = await response.json();
    if (!bodies || sections.some(section => typeof bodies[section] !== 'string')) {
      throw new Error('Cart section read returned an incomplete payload.');
    }
    return bodies;
  }

  async function completeConfirmedAddResponse({
    cart,
    sectionValue,
    sectionsUrl,
    fetchImpl,
    retryDelayMs = 80,
  }) {
    let sections;
    try {
      sections = await readSections(fetchImpl, sectionValue, sectionsUrl);
    } catch (firstError) {
      await new Promise(resolve => setTimeout(resolve, retryDelayMs));
      try {
        sections = await readSections(fetchImpl, sectionValue, sectionsUrl);
      } catch (_) {
        throw firstError;
      }
    }
    const success = {
      ...cart,
      items: cart.items,
      sections,
      _gs_players_pack_authoritative_cart: true,
    };
    return new Response(JSON.stringify(success), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  function confirmedAddPresentationFallback(cart) {
    return new Response(JSON.stringify({
      ...cart,
      items: cart.items,
      _gs_players_pack_authoritative_cart: true,
      _gs_players_pack_presentation_failed: true,
    }), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  function writeCartCounters(items) {
    if (typeof document === 'undefined') return;
    const count = shopperItemCount(items);
    const counters = document.querySelectorAll('.cart-count-badge, .cart-drawer__title-counter, .cart__title-counter');
    for (const counter of counters) counter.textContent = count > 99 ? '+' : String(count);
    for (const badge of document.querySelectorAll('.cart-count-badge')) badge.classList.toggle('hidden', count === 0);
  }

  const normalizedCopy = value => String(value ?? '').replace(/\s+/g, ' ').trim();

  function componentTitle(line) {
    return normalizedCopy(line?.product_title ?? line?.productTitle ?? line?.product?.title);
  }

  function escapedPattern(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function isCustomilyAddOnCopyForTitle(copy, title) {
    const normalized = normalizedCopy(copy);
    const pattern = new RegExp(`^${escapedPattern(title)}(?: - .+)? \\(\\d+\\) \\+\\$\\d+(?:\\.\\d{2})?$`);
    return pattern.test(normalized);
  }

  function resetOwnedCustomilyAddOns() {
    if (typeof document === 'undefined') return;
    for (const property of document.querySelectorAll('.customily-property[data-gs-players-pack-addon-hidden="true"]')) {
      property.hidden = false;
      property.style?.removeProperty?.('display');
      property.removeAttribute('data-gs-players-pack-addon-hidden');
    }
  }

  const CART_PRESENTATION_SURFACE_SELECTOR = '.cart__items, #main-cart-items, .cart-drawer__items, cart-drawer, .cart-drawer';

  function cartPresentationSurfaces(groupRows = []) {
    return [...new Set(groupRows
      .map(row => row?.closest?.(CART_PRESENTATION_SURFACE_SELECTOR))
      .filter(Boolean))];
  }

  const CUSTOMILY_ADD_ON_SELECTOR = '.customily-property[cl-property^="add-on-"]';

  function directChildWithinSurface(node, cartSurface) {
    let child = node;
    while (child?.parentElement && child.parentElement !== cartSurface) child = child.parentElement;
    return child?.parentElement === cartSurface ? child : null;
  }

  function customilyBundleAddOnCandidates(cartSurface, groupRows = []) {
    const children = [...(cartSurface?.children ?? [])];
    const groupRoots = [...new Set(groupRows
      .map(row => directChildWithinSurface(row, cartSurface))
      .filter(Boolean))];
    if (!children.length || !groupRoots.length) return [];

    const lastGroupIndex = Math.max(...groupRoots.map(root => children.indexOf(root)));
    if (lastGroupIndex < 0) return [];

    const candidates = [];
    for (const child of children.slice(lastGroupIndex + 1)) {
      if (child.matches?.('[data-players-pack-marked-line], .cart-item') || child.querySelector?.('[data-players-pack-marked-line]')) break;
      const matches = child.matches?.(CUSTOMILY_ADD_ON_SELECTOR)
        ? [child]
        : [...(child.querySelectorAll?.(CUSTOMILY_ADD_ON_SELECTOR) ?? [])];
      if (!matches.length) break;
      candidates.push(...matches);
    }
    return candidates;
  }

  function suppressExactCustomilyBundleAddOns(componentLines, candidates = []) {
    if (typeof document === 'undefined') return false;
    const titles = componentLines.map(componentTitle);
    if (titles.length !== 3 || titles.some(title => !title)) return false;

    const available = candidates.filter(candidate => candidate.dataset.gsPlayersPackAddonHidden !== 'true');
    const selected = [];
    for (const title of titles) {
      const match = available.find(candidate => {
        if (selected.includes(candidate)) return false;
        const copy = normalizedCopy(candidate.textContent);
        return isCustomilyAddOnCopyForTitle(copy, title);
      });
      if (!match) return false;
      selected.push(match);
    }

    for (const property of selected) {
      property.hidden = true;
      property.style?.setProperty?.('display', 'none', 'important');
      property.dataset.gsPlayersPackAddonHidden = 'true';
    }
    return true;
  }

  function suppressExactCustomilyBundleAddOnsAcrossSurfaces(componentLines, cartSurfaces = [], groupRows = []) {
    return cartSurfaces.reduce((count, cartSurface) => count + Number(suppressExactCustomilyBundleAddOns(
      componentLines,
      customilyBundleAddOnCandidates(cartSurface, groupRows),
    )), 0);
  }

  function presentationCartMatchesRenderedRows(cart) {
    if (typeof document === 'undefined') return false;
    const renderedRows = [...document.querySelectorAll('.cart-item[data-key][data-cart-line-quantity]')];
    const cartLines = (Array.isArray(cart?.items) ? cart.items : [])
      .map(line => `${asId(line.key)}\u0000${Number(line.quantity)}`)
      .filter(signature => !signature.startsWith('\u0000'))
      .sort();
    const surfaces = [...new Set(renderedRows
      .map(row => row.closest?.(CART_PRESENTATION_SURFACE_SELECTOR))
      .filter(Boolean))];
    if (!surfaces.length || !cartLines.length) return false;

    return surfaces.every(surface => {
      const surfaceLines = renderedRows
        .filter(row => row.closest?.(CART_PRESENTATION_SURFACE_SELECTOR) === surface)
        .map(row => `${asId(row.dataset.key)}\u0000${Number(row.dataset.cartLineQuantity)}`)
        .filter(signature => !signature.startsWith('\u0000'))
        .sort();
      return surfaceLines.length === cartLines.length
        && surfaceLines.every((signature, index) => signature === cartLines[index]);
    });
  }

  function applyCachedCartPresentation() {
    if (!lastValidatedPresentationCart || !presentationCartMatchesRenderedRows(lastValidatedPresentationCart)) return false;
    applyCartPresentation(lastValidatedPresentationCart);
    return true;
  }

  function applyCartPresentation(cart) {
    presentationRevision += 1;
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const validation = validateMarkedCart({ items });
    if (typeof document === 'undefined') return validation;

    resetOwnedCustomilyAddOns();
    const rows = [...document.querySelectorAll('[data-players-pack-marked-line]')];
    for (const row of rows) {
      row.hidden = false;
      row.style.removeProperty('display');
      row.removeAttribute('data-players-pack-group-id');
      for (const control of row.querySelectorAll('[data-players-pack-group-id]')) {
        control.removeAttribute('data-players-pack-group-id');
      }
      for (const summary of row.querySelectorAll('[data-players-pack-summary]')) summary.hidden = true;
    }

    writeCartCounters(items);
    if (!validation.valid) {
      lastValidatedPresentationCart = null;
      markCartInvalid();
      clearCartPresentationPending();
      return validation;
    }

    for (const groupId of validation.groups) {
      const lines = groupLines({ items }, groupId);
      const group = validateGroup({ items }, groupId, Number(lines[0]?.quantity));
      if (!group.valid || !group.parent) {
        lastValidatedPresentationCart = null;
        markCartInvalid();
        clearCartPresentationPending();
        return group;
      }
      const parentKey = asId(group.parent.key);
      const componentLines = group.lines.filter(line => line !== group.parent);
      const groupKeys = new Set(group.lines.map(line => asId(line.key)));
      const groupRows = rows.filter(row => groupKeys.has(asId(row.dataset.key)));
      suppressExactCustomilyBundleAddOnsAcrossSurfaces(componentLines, cartPresentationSurfaces(groupRows), groupRows);
      const componentKeys = new Set(componentLines.map(line => asId(line.key)));
      for (const row of rows) {
        const rowKey = asId(row.dataset.key);
        if (componentKeys.has(rowKey)) {
          row.hidden = true;
          row.style.setProperty('display', 'none', 'important');
          continue;
        }
        if (rowKey !== parentKey) continue;
        row.dataset.playersPackGroupId = groupId;
        for (const summary of row.querySelectorAll('[data-players-pack-summary]')) summary.hidden = false;
        for (const control of row.querySelectorAll('[name="updates[]"], cart-remove-button')) {
          control.dataset.playersPackGroupId = groupId;
        }
      }
    }
    lastValidatedPresentationCart = cart;
    clearCartPresentationPending();
    return validation;
  }

  function isCartCheckoutForm(form) {
    const action = form?.getAttribute?.('action');
    if (!action) return false;
    try {
      const path = new URL(action, location.origin).pathname.replace(/\/+$/, '');
      const root = String(globalThis.Shopify?.routes?.root || '/').replace(/^\/?/, '/').replace(/\/+$/, '');
      return path === `${root}/cart`.replace(/\/{2,}/g, '/');
    } catch (_) {
      return false;
    }
  }

  function clearCartPresentationPending() {
    if (typeof document === 'undefined') return;
    delete document.documentElement.dataset.playersPackCartPending;
  }

  function installCartCheckoutGuard() {
    if (typeof window === 'undefined' || window.__gsPlayersPackCheckoutGuardInstalled) return;
    window.__gsPlayersPackCheckoutGuardInstalled = true;
    document.addEventListener('submit', event => {
      const root = document.documentElement.dataset;
      const blocked = root.playersPackCartPending === 'true' || root.playersPackCartInvalid === 'true';
      if (blocked && isCartCheckoutForm(event.target)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
  }

  function markCartInvalid(message = BLOCKED_MESSAGE) {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.playersPackCartInvalid = 'true';
    for (const checkout of document.querySelectorAll('button[name="checkout"], [href*="/checkout"]')) {
      checkout.setAttribute('aria-disabled', 'true');
      if (checkout instanceof HTMLButtonElement) checkout.disabled = true;
    }
    for (const accelerated of document.querySelectorAll('.additional-checkout-buttons, [data-shopify="payment-button"], shopify-accelerated-checkout, shopify-accelerated-checkout-cart')) {
      accelerated.setAttribute('aria-hidden', 'true');
      accelerated.hidden = true;
      accelerated.style.pointerEvents = 'none';
    }
    installCartCheckoutGuard();
    for (const region of document.querySelectorAll('[data-players-pack-error]')) {
      region.textContent = message;
      region.hidden = false;
    }
  }

  function propertyNameFromInputName(name) {
    const match = String(name ?? '').match(/^properties\[([\s\S]*)\]$/);
    return match?.[1] ?? '';
  }

  function liveComponentUploadKeys() {
    if (typeof document === 'undefined') return {};
    const sections = [...document.querySelectorAll('#cl_optionsapp .customily_section')];
    const keys = {};
    for (const [component, label] of Object.entries(SECTION_LABEL_BY_COMPONENT)) {
      const guidedScope = GUIDED_SCOPE_BY_COMPONENT[component];
      const stampedSections = sections.filter(section => section.dataset?.gsPlayersPackV8Component === guidedScope);
      const matchingSections = stampedSections.length
        ? stampedSections
        : sections.filter(section => String(section.innerText ?? section.textContent ?? '').includes(label));
      if (matchingSections.length !== 1) return {};
      const inputs = matchingSections[0].querySelectorAll('input[type="file"]');
      if (inputs.length !== 1) return {};
      const propertyName = propertyNameFromInputName(inputs[0].name);
      if (!propertyName) return {};
      keys[component] = `_${propertyName}`;
    }
    return keys;
  }

  function formDataPayload(body, resolvedUploadKeys) {
    const properties = {};
    let hasNonemptyUpload = false;
    let id = '';
    let quantity = 1;
    let sections;
    let sectionsUrl;
    for (const [key, value] of body.entries()) {
      if (key === 'id') id = asId(value);
      else if (key === 'quantity') quantity = Number(value);
      else if (key === 'sections') sections = value;
      else if (key === 'sections_url') sectionsUrl = value;
      else if (key.startsWith('properties[') && key.endsWith(']')) {
        const propertyName = key.slice(11, -1);
        if (typeof File !== 'undefined' && value instanceof File) {
          if (value.size > 0) hasNonemptyUpload = true;
          continue;
        }
        properties[propertyName] = value;
      }
    }
    if (!id) return null;
    const item = { id, quantity, properties };
    const payload = { items: [item] };
    if (sections !== undefined) payload.sections = sections;
    if (sectionsUrl !== undefined) payload.sections_url = sectionsUrl;
    if (id !== CONTRACT.parentVariantId) return payload;
    if (hasNonemptyUpload) throw new Error('Players Pack contains an unprocessed upload. Nothing was added.');
    if (properties['Choose if you want same image on all or different'] === 'Different image on all 3 products') {
      const componentUploadKeys = resolvedUploadKeys ?? liveComponentUploadKeys();
      for (const component of Object.keys(METHOD_PROPERTY_BY_COMPONENT)) {
        const uploadProperty = componentUploadKeys[component];
        // After a completed Same Design add, Customily can remount the Different
        // Design file controls without serializing every processed control as a
        // zero-byte FormData placeholder. The scoped live key is still exact,
        // while uploadContract() below independently requires its canonical
        // method mapping and a non-empty generated private upload URL.
        if (!uploadProperty) throw new Error(`Players Pack ${component} upload mapping is missing.`);
      }
      item._gsComponentUploadKeys = { ...componentUploadKeys };
    }
    return payload;
  }

  function searchParamsPayload(params, resolvedUploadKeys) {
    const body = new FormData();
    for (const [key, value] of params.entries()) body.append(key, value);
    return formDataPayload(body, resolvedUploadKeys);
  }

  async function parseAddPayload(input, init, resolvedUploadKeys) {
    const method = String(init?.method ?? input?.method ?? 'GET').toUpperCase();
    if (method !== 'POST') return null;
    const body = init?.body;
    if (typeof FormData !== 'undefined' && body instanceof FormData) return formDataPayload(body, resolvedUploadKeys);
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return searchParamsPayload(body, resolvedUploadKeys);
    const text = typeof body === 'string'
      ? body
      : input instanceof Request
        ? await input.clone().text()
        : null;
    if (typeof text !== 'string') return null;
    try {
      return JSON.parse(text);
    } catch (_) {
      const params = new URLSearchParams(text);
      return params.has('id') ? searchParamsPayload(params, resolvedUploadKeys) : null;
    }
  }

  function exactComponentPayload(payload) {
    if (!payload || !Array.isArray(payload.items) || payload.items.length !== 3) return false;
    const ids = payload.items.map(lineVariantId).sort();
    const expected = Object.entries(CONTRACT.variants)
      .filter(([, definition]) => definition.role === 'component')
      .map(([variantId]) => variantId)
      .sort();
    return JSON.stringify(ids) === JSON.stringify(expected);
  }

  function mergeBufferedComponents(payload, bufferedComponents, now = Date.now()) {
    if (payload?.items?.length !== 1) return payload;
    if (!bufferedComponents || now - bufferedComponents.at > COMPONENT_HANDOFF_TTL_MS) {
      throw new Error('Players Pack components were not received. Nothing was added.');
    }
    return { ...payload, items: [payload.items[0], ...bufferedComponents.items] };
  }

  function syntheticBufferedResponse() {
    return new Response(JSON.stringify({ items: [] }), {
      status: 200,
      headers: { 'Content-Type': 'application/json', 'X-GS-Players-Pack-Buffered': 'true' },
    });
  }

  function payloadContainsPlayersPackVariant(payload) {
    return Array.isArray(payload?.items) && payload.items.some(item => Boolean(CONTRACT.variants[lineVariantId(item)]));
  }

  function syntheticBlockedResponse(message = BLOCKED_MESSAGE) {
    return new Response(JSON.stringify({ items: [], blocked: true, message }), {
      status: 200,
      headers: { 'Content-Type': 'application/json', 'X-GS-Players-Pack-Blocked': 'true' },
    });
  }

  function isCartAdd(input) {
    const raw = typeof input === 'string' || input instanceof URL ? String(input) : input?.url;
    if (!raw) return false;
    try {
      return /\/cart\/add(?:\.js)?$/.test(new URL(raw, location.origin).pathname);
    } catch (_) {
      return false;
    }
  }

  function replaceRequestBody(input, init, payload) {
    const body = JSON.stringify(payload);
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', 'application/json');
    return ['/cart/add.js?customily_upsell', {
      ...init,
      method: 'POST',
      credentials: init?.credentials ?? (input instanceof Request ? input.credentials : 'same-origin') ?? 'same-origin',
      headers,
      body,
    }];
  }

  function targetProductMatches(config) {
    if (asId(config?.productId) !== CONTRACT.productId) return false;
    const root = document.querySelector('[data-main-product][data-product-id][data-product-handle]');
    return root?.dataset.productId === CONTRACT.productId;
  }

  function lockVendorRepairHook() {
    window.customilyShopify ??= {};
    window.customilyShopify.hooks ??= {};
    const noRepair = items => {
      if (!Array.isArray(items)) {
        markCartInvalid('Players Pack personalization returned an invalid cart handoff. Nothing else was written.');
      }
      return null;
    };
    try {
      Object.defineProperty(window.customilyShopify.hooks, 'onCartItems', {
        configurable: false,
        enumerable: true,
        get: () => noRepair,
        set: () => {},
      });
    } catch (_) {
      window.customilyShopify.hooks.onCartItems = noRepair;
    }
  }

  function installProductAdapter(config) {
    if (typeof window === 'undefined' || !targetProductMatches(config) || window.__gsPlayersPackFetchInstalled) return false;
    window.__gsPlayersPackFetchInstalled = true;
    lockVendorRepairHook();
    const nativeFetch = window.fetch.bind(window);
    let addInFlight = false;
    let bufferedComponents = null;
    let submittedUploadKeys = null;
    let submittedUploadKeysAt = 0;

    const clearSubmittedUploadKeys = () => {
      submittedUploadKeys = null;
      submittedUploadKeysAt = 0;
    };

    const freshSubmittedUploadKeys = () => {
      if (!submittedUploadKeys || Date.now() - submittedUploadKeysAt > COMPONENT_HANDOFF_TTL_MS) {
        clearSubmittedUploadKeys();
        return null;
      }
      return submittedUploadKeys;
    };

    // Customily may remount its option controls between its component handoff
    // request and the parent request. Snapshot the exact guided component scopes
    // at the one native submit boundary, then consume that snapshot only for the
    // matching parent add. This keeps the mapping tied to the shopper-approved
    // form state without inventing or mutating Customily controls.
    document.addEventListener('submit', event => {
      const form = event.target;
      if (!form?.matches?.('form[action*="/cart/add"]')) return;
      const variantId = asId(form.querySelector?.('[name="id"]')?.value);
      if (variantId !== CONTRACT.parentVariantId) return;
      submittedUploadKeys = liveComponentUploadKeys();
      submittedUploadKeysAt = Date.now();
    }, true);

    document.addEventListener('change', event => {
      if (event.target?.name === 'properties[Choose if you want same image on all or different]') {
        clearSubmittedUploadKeys();
      }
    }, true);

    window.fetch = async function playersPackFetch(input, init) {
      if (!isCartAdd(input)) return nativeFetch(input, init);
      const requestHeaders = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
      if (requestHeaders.get('X-GS-Players-Pack-Mutation') === 'quantity') return nativeFetch(input, init);

      let payload;
      try {
        payload = await parseAddPayload(input, init, freshSubmittedUploadKeys());
        if (payload?.items?.some(item => lineVariantId(item) === CONTRACT.parentVariantId)) {
          clearSubmittedUploadKeys();
        }
      } catch (error) {
        bufferedComponents = null;
        clearSubmittedUploadKeys();
        const message = error instanceof Error ? error.message : BLOCKED_MESSAGE;
        markCartInvalid(message);
        return syntheticBlockedResponse(message);
      }
      if (!payloadContainsPlayersPackVariant(payload)) return nativeFetch(input, init);

      try {
        if (exactComponentPayload(payload)) {
          if (bufferedComponents) {
            bufferedComponents = null;
            throw new Error('Players Pack component handoff was duplicated. Nothing was added.');
          }
          const quantities = payload.items.map(item => Number(item.quantity ?? 1));
          const upsellIds = payload.items.map(item => lineProperties(item)['_customily-upsell-id']);
          if (
            quantities.some(quantity => !Number.isInteger(quantity) || quantity < 1 || quantity > 999) ||
            new Set(quantities).size !== 1 ||
            upsellIds.some(upsellId => !upsellId) ||
            new Set(upsellIds).size !== 1
          ) {
            bufferedComponents = null;
            throw new Error('Players Pack components are inconsistent. Nothing was added.');
          }
          bufferedComponents = { items: payload.items.map(item => ({ ...item, properties: { ...lineProperties(item) } })), at: Date.now() };
          return syntheticBufferedResponse();
        }

        const parentItems = payload.items.filter(item => lineVariantId(item) === CONTRACT.parentVariantId);
        if (parentItems.length === 0) {
          bufferedComponents = null;
          throw new Error('Players Pack component handoff is malformed. Nothing was added.');
        }
        let candidate = payload;
        if (payload.items.length === 1) {
          candidate = mergeBufferedComponents(payload, bufferedComponents);
          bufferedComponents = null;
        }
        if (addInFlight) throw new Error('A Players Pack add is already in progress.');
        addInFlight = true;
        try {
          const before = await readCart(nativeFetch);
          const cartValidation = validateMarkedCart(before);
          if (!cartValidation.valid) throw new Error('The existing Players Pack cart state is inconsistent.');
          const transformed = normalizeCandidateItems(candidate);
          if (groupLines(before, transformed.groupId).length) throw new Error('Players Pack bundle ID collision.');
          const [nextInput, nextInit] = replaceRequestBody(input, init, transformed);
          let response = null;
          let transportError = null;
          try {
            response = await nativeFetch(nextInput, nextInit);
          } catch (error) {
            transportError = error;
          }
          const after = await readCart(nativeFetch);
          const validation = validateGroup(after, transformed.groupId, transformed.quantity);
          if (validation.valid && sameOrdinaryState(before, after, transformed.groupId)) {
            try {
              const completed = await completeConfirmedAddResponse({
                cart: after,
                sectionValue: transformed.sections,
                sectionsUrl: transformed.sections_url,
                fetchImpl: nativeFetch,
              });
              window.__gsPlayersPackPresentationPending = false;
              return completed;
            } catch (presentationError) {
              window.__gsPlayersPackPresentationPending = true;
              return confirmedAddPresentationFallback(after);
            }
          }
          const noDelta = groupLines(after, transformed.groupId).length === 0 && sameOrdinaryState(before, after, transformed.groupId);
          if (noDelta && response && !response.ok) throw new Error(`Players Pack add was blocked with HTTP ${response.status}.`);
          throw transportError ?? new Error('Players Pack add returned an inconsistent cart state.');
        } finally {
          addInFlight = false;
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : BLOCKED_MESSAGE;
        markCartInvalid(message);
        return syntheticBlockedResponse(message);
      }
    };
    return true;
  }

  async function mutateGroup({ fetchImpl, groupId, targetQuantity, sections = [], sectionsUrl = '/cart' }) {
    const before = await readCart(fetchImpl);
    const currentLines = groupLines(before, groupId);
    const currentQuantity = Number(currentLines[0]?.quantity);
    const preValidation = validateGroup(before, groupId, currentQuantity);
    if (!preValidation.valid) throw new Error('This Players Pack is out of sync. Remove it and add it again before changing quantity.');
    if (!Number.isInteger(targetQuantity) || targetQuantity < 0 || targetQuantity > 999) throw new Error('Players Pack quantity is invalid.');
    if (targetQuantity === currentQuantity) return { cart: before, response: null };

    let response = null;
    let transportError = null;
    try {
      // Existing customization must not be regenerated by the PDP order interceptor.
      // Its own ATC bypass also satisfies the separate upsell interceptor; keep
      // add.js for increases so Shopify still validates inventory.
      if (targetQuantity > currentQuantity) {
        const delta = targetQuantity - currentQuantity;
        const parent = preValidation.parent;
        const items = [parent, ...currentLines.filter(line => line !== parent)].map(line => {
          const item = { id: lineVariantId(line), quantity: delta, properties: { ...lineProperties(line) } };
          if (line !== parent) item.parent_id = CONTRACT.parentVariantId;
          return item;
        });
        response = await fetchImpl('/cart/add.js?customily_upsell_atc=1', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-GS-Players-Pack-Mutation': 'quantity' },
          body: JSON.stringify({ items, sections, sections_url: sectionsUrl }),
        });
      } else {
        const updates = Object.fromEntries(currentLines.map(line => [line.key, targetQuantity]));
        response = await fetchImpl('/cart/update.js?customily_upsell', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections, sections_url: sectionsUrl }),
        });
      }
    } catch (error) {
      transportError = error;
    }

    const after = await readCart(fetchImpl);
    const validation = validateGroup(after, groupId, targetQuantity, { allowMissing: targetQuantity === 0 });
    if (validation.valid && sameOrdinaryState(before, after, groupId)) return { cart: after, response };
    throw transportError ?? new Error('Players Pack update returned an inconsistent cart state.');
  }

  const api = Object.freeze({
    CONTRACT,
    MARKER_PROPERTY,
    PRODUCT_PROPERTY,
    BLOCKED_MESSAGE,
    COMPONENT_HANDOFF_TTL_MS,
    isMarkedLine,
    normalizeCandidateItems,
    liveComponentUploadKeys,
    formDataPayload,
    mergeBufferedComponents,
    validateGroup,
    validateMarkedCart,
    shopperItemCount,
    sameOrdinaryState,
    readCart,
    readSections,
    completeConfirmedAddResponse,
    confirmedAddPresentationFallback,
    cartPresentationSurfaces,
    customilyBundleAddOnCandidates,
    suppressExactCustomilyBundleAddOns,
    suppressExactCustomilyBundleAddOnsAcrossSurfaces,
    resetOwnedCustomilyAddOns,
    presentationCartMatchesRenderedRows,
    applyCachedCartPresentation,
    applyCartPresentation,
    getPresentationRevision: () => presentationRevision,
    markCartInvalid,
    isCartCheckoutForm,
    installProductAdapter,
    mutateGroup,
  });

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') {
    window.GSPlayersPack = api;
    installCartCheckoutGuard();
    const script = document.currentScript;
    if (script?.dataset.playersPackProductId) {
      installProductAdapter({ productId: script.dataset.playersPackProductId, handle: script.dataset.playersPackHandle });
    }
    let presentationRefreshTimer = null;
    let presentationRefreshGeneration = 0;
    const refreshPresentation = () => {
      if (!document.querySelector('[data-players-pack-marked-line]')) {
        clearCartPresentationPending();
        return;
      }
      const requestGeneration = ++presentationRefreshGeneration;
      const revision = presentationRevision;
      readCart(window.fetch.bind(window))
        .then(cart => {
          if (requestGeneration === presentationRefreshGeneration && revision === presentationRevision) applyCartPresentation(cart);
        })
        .catch(() => {
          if (requestGeneration === presentationRefreshGeneration && revision === presentationRevision) {
            markCartInvalid();
            clearCartPresentationPending();
          }
        });
    };
    const installGuard = () => {
      refreshPresentation();
      const observer = new MutationObserver(mutations => {
        const needsPresentation = mutations.some(mutation => [...mutation.addedNodes].some(node =>
          node instanceof Element && (
            node.matches('[data-players-pack-marked-line], .customily-property[cl-property^="add-on-"]') ||
            node.querySelector('[data-players-pack-marked-line], .customily-property[cl-property^="add-on-"]')
          )
        ));
        if (!needsPresentation) return;
        // Customily appends its display-only add-on rows after the cart has
        // already rendered. Reapply the last validated cart synchronously so
        // those rows are suppressed in this observer microtask, before paint.
        // A changed/remounted cart cannot use the snapshot because its marked
        // line keys will differ; that path still performs an authoritative read.
        if (applyCachedCartPresentation()) return;
        window.clearTimeout(presentationRefreshTimer);
        presentationRefreshTimer = window.setTimeout(refreshPresentation, 0);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', installGuard, { once: true });
    else installGuard();
  }
})();
